import React, { useCallback } from 'react';
import { ProductDetail } from '../../types/ProductTypes';
import {
    approvedStatusToIconMapper,
    marpolRegulationToIconMapper,
    cleaningDifficultyToIconMapper,
    imdgCodeClassToIconMapper,
    marpolPollutionEnumToStringMapper,
    ibcShipTypeEnumToStringMapper,
} from '../../utils/iconMappers';
import { SymbolBarContainer, SymbolButton, SymbolImage, SymbolText } from './SymbolBar.styles';
import { resolvePopoverText } from './PopoverTexts';
import InfoPopover from '../common/InfoPopover';

interface SymbolBarProps {
    product: ProductDetail;
    canEdit?: boolean;
}

interface SymbolItem {
    title: string;
    tooltip: string;
    link?: string;
    popover?: string;
    popoverInnerText?: string;
    icon?: string;
    text?: string;
}

const SymbolBar: React.FC<SymbolBarProps> = ({ product, canEdit = false }) => {
    const generateSymbolBar = (): SymbolItem[] => {
        const symbols: SymbolItem[] = [];

        if (!product) return symbols;

        // Approved Status
        if (canEdit) {
            symbols.push({
                title: 'Approved Status',
                tooltip: product.isApproved ? 'Approved' : 'Not Approved',
                link: 'status',
                popover: `status.approved${product.isApproved}`,
                icon: approvedStatusToIconMapper(product.isApproved),
            });
        }

        // Regulation
        if (product.category?.regulations?.marpolRegulation && product.category.regulations.marpolRegulation !== 0) {
            const regNameMap: Record<number, string> = {
                1: 'MARPOL Annex I',
                2: 'IBC Code (MARPOL Annex II) - Chapter 17',
                3: 'IBC Code (MARPOL Annex II) - Chapter 18',
                4: 'Gas (IGC Code)',
            };
            const regName = regNameMap[product.category.regulations.marpolRegulation] || 'Regulation';
            symbols.push({
                title: 'Regulation',
                tooltip: regName,
                link: 'rulesAndRegulations',
                popover: `generalInformation.regulation${product.category.regulations.marpolRegulation}`,
                icon: marpolRegulationToIconMapper(product.category.regulations.marpolRegulation),
            });
        }

        // Pollution Category
        if (product.category?.regulations?.marpolPollutionCategory && product.category.regulations.marpolPollutionCategory !== 0) {
            const polLabel = marpolPollutionEnumToStringMapper(product.category.regulations.marpolPollutionCategory);
            symbols.push({
                title: 'Pollution Category',
                tooltip: `Pollution Category ${polLabel}`,
                link: 'rulesAndRegulations',
                popover: `rulesAndRegulations.pollutionCategory${product.category.regulations.marpolPollutionCategory}`,
                text: polLabel,
            });
        }

        // Ship Type (for IBC regulation - Annex II Chapter 17 and 18)
        const isIBCRegulation = product.category?.regulations?.marpolRegulation === 2 || product.category?.regulations?.marpolRegulation === 3;
        if (isIBCRegulation && product.category?.regulations?.ibcRegulation?.shipType) {
            const shipLabel = ibcShipTypeEnumToStringMapper(product.category.regulations.ibcRegulation.shipType);
            symbols.push({
                title: 'Ship Type',
                tooltip: `Ship Type ${shipLabel}`,
                link: 'rulesAndRegulations',
                popover: `rulesAndRegulations.shipType${product.category.regulations.ibcRegulation.shipType}`,
                text: shipLabel,
            });
        }

        // USCG Group
        if (product.regulations?.uscgGroup) {
            symbols.push({
                title: 'USCG Group',
                tooltip: `USCG Group ${product.regulations.uscgGroup.number} - ${product.regulations.uscgGroup.name}`,
                link: 'uscgCompatibility',
                popoverInnerText: `${product.regulations.uscgGroup.number} - ${product.regulations.uscgGroup.name}`,
                text: `<div class='USCGTitle'>USCG</div><div class='USCGGroup'>${product.regulations.uscgGroup.number}</div>`,
            });
        }

        // Warning — IBC specific requirements (16.2.6, 16.2.7, 16.2.9, 15.13) + manual warning comment (category 5)
        {
            const ibcReqs = product.category?.regulations?.ibcRegulation?.specificAndOperationalRequirements || [];
            const ibcWarningMap: Record<string, string> = {
                'IBC-16.2.6': 'The IBC Specific Requirement 16.2.6 applies to this product.',
                'IBC-16.2.7': 'The IBC Specific Requirement 16.2.7 applies to this product.',
                'IBC-16.2.9': 'The IBC Specific Requirement 16.2.9 applies to this product.',
                'IBC-15.13': 'The IBC Specific Requirement 15.13 applies to this product.',
            };
            const ibcWarnings = ibcReqs
                .filter((r) => ibcWarningMap[r.paragraphNumber])
                .map((r) => ibcWarningMap[r.paragraphNumber]);
            const warningComment = product.comments?.find((c) => c.category === 5);
            const manualWarning = warningComment?.text?.trim() || '';
            const allWarnings = [...ibcWarnings, manualWarning].filter((s) => s);
            if (allWarnings.length > 0) {
                symbols.push({
                    title: 'Warning',
                    tooltip: 'Product has warnings',
                    link: 'generalInformation',
                    popoverInnerText: allWarnings.join('\n'),
                    icon: '/assets/images/Warning.png',
                });
            }
        }

        // Check for flash point warnings
        if (product.physicalData?.flashPointRange) {
            const flashPoint = product.physicalData.flashPointRange.minValue?.value || product.physicalData.flashPointRange.maxValue?.value;
            if (flashPoint && flashPoint < 60) {
                const popoverContent =
                    product.category?.regulations?.marpolRegulation === 2 ? 'warnings.lowFlashPointWarningIBC' : 'warnings.lowFlashPointWarningSOLAS';

                symbols.push({
                    title: 'Low Flash Point Warning',
                    tooltip: `Low Flash Point (${flashPoint}°C)`,
                    link: 'physicalProperties',
                    popover: popoverContent,
                    icon: '/assets/images/LowFlashPointWarning.png',
                });
            }
        }

        // Static Accumulator Warning
        if (product.category?.isStaticAccumulator === 1) {
            symbols.push({
                title: 'Static Accumulator Warning',
                tooltip: 'Static Accumulator',
                link: 'physicalProperties',
                popover: 'warnings.staticAccumulatorWarning',
                icon: '/assets/images/StaticAccumulatorWarning.png',
            });
        }

        // Cleaning Difficulty
        const cleaningProcedure = product.procedures?.find((procedure) => procedure.category === 1);
        if (cleaningProcedure && cleaningProcedure.score && cleaningProcedure.score > 0) {
            const difficultyLabels: Record<number, string> = { 1: 'Easy', 2: 'Medium', 3: 'Difficult' };
            symbols.push({
                title: 'Cleaning Difficulty',
                tooltip: `Cleaning Difficulty: ${difficultyLabels[cleaningProcedure.score] || cleaningProcedure.score}`,
                link: 'cleaningProcedure',
                popover: `cleaningProcedures.cleaningDifficulty${cleaningProcedure.score}`,
                icon: cleaningDifficultyToIconMapper(cleaningProcedure.score),
            });
        }

        // IMDG Class
        if (product.regulations?.imdgCodeClass) {
            symbols.push({
                title: 'IMDG Class',
                tooltip: `IMDG Class ${product.regulations.imdgCodeClass.classification}`,
                link: 'emergencyScenarios',
                popover: `generalInformation.hazards${product.regulations.imdgCodeClass.classification?.[0]}`,
                icon: imdgCodeClassToIconMapper(product.regulations.imdgCodeClass.classification),
            });
        }

        return symbols;
    };

    const symbols = generateSymbolBar();

    if (symbols.length === 0) {
        return null;
    }

    const getPopoverHtml = useCallback((symbol: SymbolItem): string => {
        if (symbol.popover) {
            const richContent = resolvePopoverText(symbol.popover);
            if (richContent) return richContent;
        }
        if (symbol.popoverInnerText) return symbol.popoverInnerText;
        return symbol.tooltip;
    }, []);

    return (
        <SymbolBarContainer>
            {symbols.map((symbol, index) => {
                const html = getPopoverHtml(symbol);
                const buttonContent = (
                    <SymbolButton>
                        {symbol.icon && <SymbolImage src={symbol.icon} alt={symbol.title} />}
                        {symbol.text && (
                            <SymbolText
                                dangerouslySetInnerHTML={{
                                    __html: symbol.text,
                                }}
                            />
                        )}
                    </SymbolButton>
                );
                return (
                    <InfoPopover key={index} trigger={buttonContent} title={symbol.title}>
                        <div dangerouslySetInnerHTML={{ __html: html }} />
                    </InfoPopover>
                );
            })}
        </SymbolBarContainer>
    );
};

export default SymbolBar;
